import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { IconSpinner } from '../../../../components';

import css from './ISOSearchBar.module.css';

// White magnifier — shown instead of the "Search" label inside the gold
// square button on mobile (hero ≤767px and the search overlay). Matches the
// approved reference (design-reference/mobile-search-overlay.html, .goSq).
// fill="none" sits on the <circle> itself (not just the <svg>) so no outside
// CSS can fill the lens.
const MagnifierIcon = ({ className }) => (
  <svg
    className={className}
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ffffff"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" fill="none" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const StarIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
  </svg>
);

/**
 * Shared keyword + ISO (AI) search bar.
 *
 * Used by both the desktop Topbar and the homepage hero so the two search
 * entry points behave identically. Submitting reuses the exact route-at-submit
 * orchestration via the `onSearchSubmit` handler (Topbar.handleSubmit →
 * util/searchSubmit.routeAndSubmitKeywordSearch). The orchestration is
 * fail-open: a failed/unrouted call still navigates to /s?keywords=<raw>, and
 * this component additionally falls back to a direct push if no handler was
 * threaded in.
 *
 * @param {Object} props
 * @param {Function} [props.onSearchSubmit] async ({ keywords }) => void
 * @param {string} [props.placeholder] input placeholder text
 * @param {('topbar'|'hero'|'overlay')} [props.variant] visual variant —
 *   'overlay' is the mobile search overlay's compact pill (flat border, gold
 *   square magnifier button)
 * @param {string} [props.className] extra class on the form root
 * @param {string} [props.ariaLabel] accessible label for the input
 * @param {Object} [props.inputRef] ref attached to the input (overlay autofocus)
 * @param {Function} [props.onEmptySubmit] () => void — replaces the default
 *   empty-submit /s push (the overlay closes itself and clears keywords)
 */
const ISOSearchBar = props => {
  const {
    onSearchSubmit,
    placeholder = ' IN SEARCH OF…',
    variant = 'topbar',
    className,
    ariaLabel = 'Search horses',
    inputRef,
    onEmptySubmit,
  } = props;

  const history = useHistory();
  const [query, setQuery] = useState('');
  // True while the AI route-at-submit call is in flight (~1–2s). Drives the
  // in-bar working state and blocks a double-submit firing two route calls.
  const [isSearching, setIsSearching] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    // Ignore a re-submit (Enter or click) while a route call is already running.
    if (isSearching) {
      return;
    }
    const q = query.trim();
    if (!q) {
      // Empty submit clears the keyword search (preserve prior behavior:
      // never invokes the route-at-submit orchestration). The overlay opts
      // into its own empty handling (close + clear) via onEmptySubmit.
      if (typeof onEmptySubmit === 'function') {
        onEmptySubmit();
      } else {
        history.push('/s');
      }
      return;
    }
    if (typeof onSearchSubmit === 'function') {
      // Route the typed query through the shared route-at-submit orchestration
      // (Story 8.2 / AC9). Awaiting lets the AI parse complete before navigation.
      // Fail-open: a failed/unrouted call still navigates to /s?keywords=<raw>.
      setIsSearching(true);
      try {
        await onSearchSubmit({ keywords: q });
      } finally {
        // A successful route navigates away and unmounts this bar; the finally
        // still clears the flag on the fail-open/throw path so the bar recovers.
        setIsSearching(false);
      }
    } else {
      // Defensive fallback if the handler wasn't threaded down.
      history.push(`/s?keywords=${encodeURIComponent(q)}`);
    }
  };

  const rootClasses = classNames(
    css.searchBar,
    {
      [css.searchBarHero]: variant === 'hero',
      [css.searchBarOverlay]: variant === 'overlay',
    },
    className
  );

  return (
    <form className={rootClasses} onSubmit={handleSubmit} role="search" style={{ outline: 'none' }}>
      {isSearching ? (
        <IconSpinner rootClassName={css.searchSpinner} />
      ) : (
        <StarIcon className={css.searchSparkle} />
      )}
      <input
        ref={inputRef}
        className={css.searchInput}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        enterKeyHint="search"
      />
      <button
        type="submit"
        className={css.searchBtn}
        aria-label={isSearching ? 'Searching' : 'Search'}
        aria-busy={isSearching}
        disabled={isSearching}
      >
        {/* Label on desktop; the hero variant swaps to the magnifier icon on
            mobile via CSS (≤767px) — same submit either way. */}
        <span className={css.searchBtnLabel}>{isSearching ? 'Searching…' : 'Search'}</span>
        <MagnifierIcon className={css.searchBtnIcon} />
      </button>
    </form>
  );
};

export default ISOSearchBar;
